import { useState, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { type CropConfig, CROP_COLOR_PALETTE } from './types';

interface Props {
  crops: CropConfig[];
  onChange: (crops: CropConfig[]) => void;
}

export function CropSetup({ crops, onChange }: Props) {
  const { t } = useTranslation();
  const [newCropName, setNewCropName] = useState('');
  const [isExpanded, setIsExpanded] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  const nextColor = CROP_COLOR_PALETTE[crops.length % CROP_COLOR_PALETTE.length];

  const addCrop = () => {
    const name = newCropName.trim();
    if (!name) return;
    const id = name.toLowerCase().replace(/[^a-z0-9]/g, '_');
    if (crops.some((c) => c.id === id)) return;
    onChange([...crops, { id, label: name, color: nextColor }]);
    setNewCropName('');
    inputRef.current?.focus();
  };

  const removeCrop = (id: string) => {
    if (crops.length <= 1) return;
    onChange(crops.filter((c) => c.id !== id));
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      addCrop();
    }
  };

  return (
    <div className="card !p-3 md:!p-4">
      <button
        onClick={() => setIsExpanded(!isExpanded)}
        className="w-full flex items-center justify-between gap-2"
      >
        <div className="flex items-center gap-2">
          <div className="w-7 h-7 rounded-lg bg-gradient-to-br from-spi-green/80 to-spi-green-dark flex items-center justify-center">
            <svg className="w-3.5 h-3.5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6V4m0 2a2 2 0 100 4m0-4a2 2 0 110 4m-6 8a2 2 0 100-4m0 4a2 2 0 110-4m0 4v2m0-6V4m6 6v10m6-2a2 2 0 100-4m0 4a2 2 0 110-4m0 4v2m0-6V4" />
            </svg>
          </div>
          <div className="text-left">
            <span className="text-sm font-semibold text-spi-green-dark">
              {t('cropSetup.title')}
            </span>
            <span className="text-xs text-spi-slate ml-2">
              {t('cropSetup.count', { count: crops.length })}
            </span>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <div className="flex -space-x-1">
            {crops.map((crop) => (
              <div
                key={crop.id}
                className="w-5 h-5 rounded-full border-2 border-white"
                style={{ backgroundColor: crop.color }}
                title={crop.label}
              />
            ))}
          </div>
          <svg
            className={`w-4 h-4 text-spi-slate transition-transform ${isExpanded ? 'rotate-180' : ''}`}
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
          </svg>
        </div>
      </button>

      {isExpanded && (
        <div className="mt-3 pt-3 border-t border-spi-parchment">
          <div className="flex flex-wrap gap-2 mb-3">
            {crops.map((crop) => (
              <div
                key={crop.id}
                className="flex items-center gap-1.5 pl-2 pr-1 py-1 rounded-full text-xs font-medium bg-white border border-gray-200"
              >
                <div
                  className="w-3 h-3 rounded-full flex-shrink-0"
                  style={{ backgroundColor: crop.color }}
                />
                <span className="text-spi-charcoal">{crop.label}</span>
                {crops.length > 1 && (
                  <button
                    onClick={() => removeCrop(crop.id)}
                    className="w-5 h-5 rounded-full flex items-center justify-center text-gray-400 hover:text-red-500 hover:bg-red-50 transition-colors"
                    title={t('cropSetup.remove', { crop: crop.label })}
                  >
                    <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                )}
              </div>
            ))}
          </div>

          <div className="flex gap-2">
            <div className="relative flex-1">
              <input
                ref={inputRef}
                type="text"
                value={newCropName}
                onChange={(e) => setNewCropName(e.target.value)}
                onKeyDown={handleKeyDown}
                placeholder={t('cropSetup.placeholder')}
                className="input text-sm py-1.5 pl-8"
                maxLength={30}
              />
              <div
                className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 rounded-full border-2"
                style={{ borderColor: nextColor, backgroundColor: `${nextColor}33` }}
              />
            </div>
            <button
              onClick={addCrop}
              disabled={!newCropName.trim() || crops.some((c) => c.id === newCropName.trim().toLowerCase().replace(/[^a-z0-9]/g, '_'))}
              className="px-3 py-1.5 rounded-lg bg-spi-green text-white text-xs font-medium hover:bg-spi-green-dark transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
            >
              {t('cropSetup.add')}
            </button>
          </div>
          <p className="text-[10px] text-spi-slate mt-2">
            {t('cropSetup.hint')}
          </p>
        </div>
      )}
    </div>
  );
}
